/**
 * `PolicyStatusCard` — proto-aligned single visual unit for the
 * policy status block on the bespoke insurance routes.
 *
 * The prototype renders the status banner, an optional clarifying
 * note, and the insurance-number row inside one light-green-tinted
 * card. Today this surface ships on `/vapaa-ajan-tapaturmavakuutus`
 * (heading + number). It also served `/oikeusturvavakuutus` until that
 * route was deleted with its article on 2026-08-28; the `body` prop
 * below is retained because the card is shared and an eligibility-gated
 * caller may want it again.
 *
 * Tokens bind to the existing `--color-warning-bg` / `--color-warning-
 * fg` pair (the muted brand-green tint) so the visual stays inside
 * the project token alphabet — no raw Tailwind palette steps and no
 * `*-[…]` brackets.
 *
 * Accessibility: rendered as a `<section>` with `aria-label` set to
 * the status heading so AT users can navigate to the policy summary
 * by landmark. The number row uses semantic `<p>` (not `<dl>`/`<dt>`/
 * `<dd>`) because the visual grouping reads as a single sentence; the
 * label "Vakuutusnumero" is part of the visible text and the
 * landmark name covers the AT use case.
 */

import type { JSX } from 'react';

import { INSURANCE_NUMBER_LABEL } from './insuranceLabel.js';

const policyStatusCardClasses =
  'rounded-md bg-warning-bg px-4 py-3 flex flex-col gap-1 text-center text-warning-fg';
const policyStatusHeadingClasses = 'text-base font-semibold';
const policyStatusBodyClasses = 'text-sm';
const policyStatusNumberClasses = 'text-sm font-medium';

export interface PolicyStatusCardProps {
  /**
   * Banner heading (Finnish). Today comes from
   * `metadata.statusBannerFi` on the bespoke insurance routes
   * (e.g. "Vakuutus on voimassa").
   */
  readonly statusHeading: string;
  /**
   * Optional clarifying body line rendered between the heading and the
   * number. `/oikeusturvavakuutus` used it to surface "Edellytykset
   * tarkistetaan aina kuitenkin tilanteen mukaan." between the status
   * heading and the policy number; since that route was deleted the prop
   * has no caller, and no surviving caller passes it.
   */
  readonly body?: string;
  /**
   * Policy / insurance number (e.g. `"353-4908380-K"`,
   * `"312-0945888-T"`). Combined with the byte-identical Finnish
   * "Vakuutusnumero" label.
   */
  readonly insuranceNumber: string;
  /** Authored label preceding the policy number; older content keeps Finnish default. */
  readonly insuranceNumberLabel?: string | undefined;
}

export function PolicyStatusCard({
  statusHeading,
  body,
  insuranceNumber,
  insuranceNumberLabel = INSURANCE_NUMBER_LABEL,
}: PolicyStatusCardProps): JSX.Element {
  return (
    <section className={policyStatusCardClasses} aria-label={statusHeading}>
      <p className={policyStatusHeadingClasses}>{statusHeading}</p>
      {body !== undefined ? <p className={policyStatusBodyClasses}>{body}</p> : null}
      <p className={policyStatusNumberClasses}>
        {insuranceNumberLabel} {insuranceNumber}
      </p>
    </section>
  );
}
